import React, { useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Container, Card, Form, Button, Spinner, Alert,
  Row, Col, Badge, ListGroup, InputGroup, Nav,
} from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import { getAssignedProjects } from '../../../services/technicianProjectService';
import { listProjects } from '../../../services/projectService';
import { createSmartRequest } from '../../../services/materialRequestService';
import { supabase } from '../../../services/supabase';
import { callMcpTool } from '../../../services/mcpClient';
import useAudioRecorder from '../../../hooks/useAudioRecorder';
import { compressImage } from '../../../utils/fileUpload';
// #599 — la busqueda de candidatos contra el catalogo. Gemelo byte a byte de
// `mcp-server/src/utils/inventorySearch.js`.
import { findInventoryCandidates } from '../../../utils/inventorySearch';
import './MaterialRequestForm.css';


// El atajo de /tecnico/materiales entra directo al modo que el tecnico eligio
// ("Dictar la lista" / "Tomarle una foto"). Sin esto, los dos botones caian en
// el modo Texto — o sea, a teclear, que es exactamente lo que el atajo evita.
// Las claves van en espanol porque son parte de una URL que el tecnico ve.
const MODE_FROM_URL = {
  dictar: 'audio',
  foto: 'photo',
  escribir: 'text',
};

const BUCKET_CONFIG = {
  matched:           { label: 'En stock',          variant: 'success', icon: 'bi-check-circle-fill' },
  fuzzy:             { label: '¿Quisiste decir…?', variant: 'warning', icon: 'bi-question-circle-fill' },
  unmatched:         { label: 'Nuevo ítem',         variant: 'secondary', icon: 'bi-pencil-fill' },
  user_confirmed_new:{ label: 'Nuevo ítem',         variant: 'secondary', icon: 'bi-pencil-fill' },
};

const FULFILLMENT_BADGE = {
  from_stock:    { text: 'Asignar del stock',    bg: 'success' },
  needs_purchase:{ text: 'Orden de compra',      bg: 'primary' },
};

export default function SmartMaterialRequest() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // 'text' | 'audio' | 'photo' — el default sigue siendo texto; ?modo= solo lo
  // adelanta cuando el tecnico ya dijo desde el atajo que va a dictar o a
  // fotografiar.
  const [inputMode, setInputMode] = useState(
    () => MODE_FROM_URL[searchParams.get('modo')] || 'text'
  );
  const [projectId, setProjectId] = useState('');
  const [text, setText] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [reviewItems, setReviewItems] = useState(null);
  const [sourceInputText, setSourceInputText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // #369 — aviso de que el "textual del líder" no coincide con lo que se tecleó.
  const [rawTextWarning, setRawTextWarning] = useState(null);

  const photoInputRef = useRef(null);

  const audio = useAudioRecorder({ maxDuration: 120 });

  // Un admin no tiene `task_assignments`, así que
  // `getAssignedProjects` le devuelve el selector vacío. Un admin ve TODOS los
  // proyectos, igual que en ExpenseForm; un técnico sigue viendo sólo los suyos.
  const { data: projectsResp } = useQuery({
    queryKey: ['projects-for-smart-request', isAdmin ? 'admin' : user?.id],
    queryFn: () => (isAdmin
      ? listProjects({ pageSize: 100 })
      : getAssignedProjects(user?.id, { pageSize: 50 })),
    enabled: !!user?.id,
  });
  const projects = projectsResp?.data || [];

  // ── Shared: call MCP extract endpoint, match items, go to review ──
  //
  // Va por `callMcpTool` y NO por un fetch a mano. El fetch que habia aca
  // apuntaba a `/api/tools/<tool>/execute`, una URL que no existe en NINGUN
  // carril: el carril real es `/api/mcp/tools/<tool>/execute` y el legacy es
  // `/api/tools/<tool>` sin `/execute`. O sea que esta pantalla entera --
  // dictar y sacar foto -- devolvia 404 y nunca funciono desde el navegador.
  // No se noto porque el 404 se mostraba como "Error al procesar", que se lee
  // como que la IA fallo y no como que la llamada no llego a ningun lado.
  //
  // Ademas mandaba `{ params }` envuelto y leia `{ data }` de la respuesta
  // cruda: dos contratos que tampoco coincidian con el carril real. Por eso el
  // arreglo no es corregir la URL sino usar el cliente que ya existe, que sabe
  // la URL, el shape, y ademas maneja el 401 de #389 (sin el, un token vencido
  // se veia igual que un fallo de extraccion).
  const runExtraction = useCallback(async (endpoint, params, srcText) => {
    setError(null);
    setExtracting(true);
    try {
      const { data: extractData, error: mcpError } = await callMcpTool(endpoint, params);
      if (mcpError) throw mcpError;
      if (!extractData?.items?.length) {
        setError('No se encontraron ítems. Intenta ser más específico.');
        return;
      }
      const matched = await matchItems(extractData.items);
      setReviewItems(matched);
      setSourceInputText(srcText || '');
    } catch (err) {
      setError(err.message || 'Error al procesar');
    } finally {
      setExtracting(false);
    }
  }, []);

  // ── Text mode ──
  const handleExtract = useCallback(() => {
    if (!text.trim()) return;
    runExtraction('extract_materials_from_text', { text }, text);
  }, [text, runExtraction]);

  // ── Audio mode ──
  const handleExtractAudio = useCallback(() => {
    if (!audio.audioBase64) return;
    runExtraction(
      'extract_materials_from_audio',
      { audio_base64: audio.audioBase64, audio_format: audio.audioFormat?.split('/')[1]?.split(';')[0] || 'webm' },
      `[Audio ${audio.formattedTime}]`
    );
  }, [audio, runExtraction]);

  // ── Photo mode ──
  const handlePhotoChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file, { maxSizeMB: 1, maxWidthOrHeight: 1280 });
      setPhotoFile(compressed);
      setPhotoPreview(URL.createObjectURL(compressed));
    } catch {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  }, []);

  const handleExtractPhoto = useCallback(() => {
    if (!photoFile) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      const fmt = photoFile.type?.split('/')[1] || 'jpeg';
      runExtraction('extract_materials_from_image', { image_base64: base64, image_format: fmt }, '[Foto]');
    };
    reader.readAsDataURL(photoFile);
  }, [photoFile, runExtraction]);

  // ── Step 2: submit the reviewed list ──
  const handleSubmit = useCallback(async () => {
    if (!projectId) { setError('Selecciona un proyecto'); return; }
    if (!reviewItems?.length) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        project_id: projectId,
        source_input_type: inputMode,
        source_input_text: sourceInputText || text,
        items: reviewItems.filter(i => !i._removed),
      };
      const { data: created, error: submitErr } = await createSmartRequest(payload);
      if (submitErr) throw submitErr;

      // La bandeja de admin tiene `staleTime: 30_000`; sin invalidarla, el
      // admin que acaba de crear una solicitud desde acá vuelve y no la ve.
      queryClient.invalidateQueries({ queryKey: ['material-requests-inbox'] });

      // #369 — el aviso se redacta ACÁ, para la persona que acaba de dictar. El
      // servicio devuelve la clasificación estructurada; el mensaje del carril
      // MCP habla de `source_input_text` y de "el líder", que no es el idioma de
      // esta pantalla.
      const bad = created?.unverified_raw_text || [];
      setRawTextWarning(
        bad.length
          ? `El asistente reescribió ${bad.length === 1 ? 'el texto de 1 ítem' : `el texto de ${bad.length} ítems`}: ` +
            bad.slice(0, 3).map((b) => `"${b.raw_text}"`).join(', ') +
            (bad.length > 3 ? '…' : '') +
            '. Se guardó tu solicitud igual, pero eso no va a figurar como tus palabras textuales.'
          : null
      );
      setSuccess(true);
      // Con aviso, la pantalla se queda más tiempo: 1,5 s no alcanza para leer
      // que el asistente reescribió lo que dijiste.
      const destino = isAdmin ? '/admin/solicitudes-materiales' : '/tecnico/materiales';
      setTimeout(() => navigate(destino), bad.length ? 6000 : 1500);
    } catch (err) {
      setError(err.message || 'Error al enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  }, [projectId, reviewItems, text, inputMode, sourceInputText, navigate, isAdmin, queryClient]);

  const updateItem = (idx, patch) =>
    setReviewItems(prev => prev.map((item, i) => i === idx ? { ...item, ...patch } : item));

  // ── Reset to input step ──
  const handleReset = () => {
    setReviewItems(null);
    setError(null);
    audio.resetRecording();
  };

  if (success) {
    return (
      <Container className="py-4" style={{ maxWidth: 600 }}>
        <Alert variant="success" className="text-center">
          <i className="bi bi-check-circle-fill me-2" />
          Solicitud enviada. El administrador recibirá una notificación.
        </Alert>
        {/* #369 — si la extracción reescribió las palabras del líder, se dice ACÁ,
            que es el único momento en que la persona que las dijo todavía está
            mirando la pantalla. Después ese texto viaja a la lista de compra y se
            lee como una cita suya. */}
        {rawTextWarning && (
          <Alert variant="warning" className="small mb-0">
            <i className="bi bi-quote me-2" />
            {rawTextWarning}
          </Alert>
        )}
      </Container>
    );
  }

  return (
    <Container className="py-3" style={{ maxWidth: 720 }}>
      <div className="d-flex align-items-center mb-3 gap-2">
        <Button variant="link" className="p-0" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left fs-5" />
        </Button>
        {/* "Solicitud inteligente" era el nombre interno de la pantalla. Quien
            llega acá viene de apretar "Dictar la lista" o "Tomarle una foto";
            el título tiene que confirmarle dónde está, no nombrar la función. */}
        <h5 className="mb-0 fw-semibold">Pedir materiales</h5>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Input phase ── */}
      {!reviewItems && (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Proyecto</Form.Label>
              <Form.Select value={projectId} onChange={e => setProjectId(e.target.value)}>
                <option value="">Selecciona un proyecto…</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.project_number} — {p.title}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Mode tabs */}
            <Nav variant="pills" className="mb-3 gap-1" activeKey={inputMode} onSelect={setInputMode}>
              <Nav.Item>
                <Nav.Link eventKey="text" className="smart-mode-tab px-3">
                  <i className="bi bi-textarea-t me-1" />Escribir
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="audio" className="smart-mode-tab px-3">
                  <i className="bi bi-mic me-1" />Dictar
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="photo" className="smart-mode-tab px-3">
                  <i className="bi bi-camera me-1" />Foto
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {/* Text mode */}
            {inputMode === 'text' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder={'Escribe o pega la lista de materiales.\nEjemplo:\n• 5 cables UTP cat6\n• 2 conectores RJ45'}
                    value={text}
                    onChange={e => setText(e.target.value)}
                  />
                  <Form.Text className="text-muted">La IA extrae y estructura la lista automáticamente.</Form.Text>
                </Form.Group>
                <Button variant="primary" className="w-100" onClick={handleExtract}
                  disabled={extracting || !text.trim() || !projectId}>
                  {extracting ? <><Spinner size="sm" className="me-2" />Procesando…</> : <><i className="bi bi-magic me-2" />Procesar con IA</>}
                </Button>
              </>
            )}

            {/* Audio mode */}
            {inputMode === 'audio' && (
              <div className="text-center">
                {!audio.isSupported && (
                  <Alert variant="warning" className="small">Tu navegador no soporta grabación de audio.</Alert>
                )}
                <div className="mb-3 d-flex flex-column align-items-center gap-2">
                  <div className="fs-1 text-muted font-monospace">{audio.formattedTime}</div>
                  {!audio.isRecording && !audio.audioBlob && (
                    <Button variant="danger" size="lg" className="rounded-circle" style={{ width: 64, height: 64 }}
                      aria-label="Grabar"
                      onClick={audio.startRecording} disabled={!audio.isSupported || !projectId}>
                      <i className="bi bi-mic-fill" aria-hidden="true" />
                    </Button>
                  )}
                  {audio.isRecording && (
                    <Button variant="secondary" size="lg" className="rounded-circle" style={{ width: 64, height: 64 }}
                      aria-label="Detener la grabación"
                      onClick={audio.stopRecording}>
                      <i className="bi bi-stop-fill" aria-hidden="true" />
                    </Button>
                  )}
                  {audio.audioUrl && !audio.isRecording && (
                    <div className="w-100">
                      <audio src={audio.audioUrl} controls className="w-100 mb-2" />
                      <div className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={audio.resetRecording} className="flex-grow-1">
                          <i className="bi bi-arrow-counterclockwise me-1" />Regrabar
                        </Button>
                        <Button variant="primary" size="sm" onClick={handleExtractAudio}
                          disabled={extracting || !projectId} className="flex-grow-1">
                          {extracting ? <><Spinner size="sm" className="me-1" />Procesando…</> : <><i className="bi bi-magic me-1" />Procesar</>}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                {audio.error && <Alert variant="danger" className="small">{audio.error}</Alert>}
                <Form.Text className="text-muted">Dicta tu lista de materiales en español (máx. 2 min).</Form.Text>
              </div>
            )}

            {/* Photo mode */}
            {inputMode === 'photo' && (
              <>
                <div className="text-center mb-3">
                  {photoPreview ? (
                    <img src={photoPreview} alt="preview" className="img-fluid rounded mb-2" style={{ maxHeight: 240 }} />
                  ) : (
                    <div className="border rounded d-flex align-items-center justify-content-center text-muted"
                      style={{ height: 160, cursor: 'pointer' }} onClick={() => photoInputRef.current?.click()}>
                      <div>
                        <i className="bi bi-camera fs-1 d-block mb-1" />
                        <small>Toca para tomar o seleccionar foto</small>
                      </div>
                    </div>
                  )}
                  <input ref={photoInputRef} type="file" accept="image/*" capture="environment"
                    className="d-none" onChange={handlePhotoChange} />
                  <div className="d-flex gap-2 mt-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => photoInputRef.current?.click()} className="flex-grow-1">
                      <i className="bi bi-image me-1" />{photoPreview ? 'Cambiar foto' : 'Seleccionar foto'}
                    </Button>
                    {photoPreview && (
                      <Button variant="primary" size="sm" onClick={handleExtractPhoto}
                        disabled={extracting || !projectId} className="flex-grow-1">
                        {extracting ? <><Spinner size="sm" className="me-1" />Procesando…</> : <><i className="bi bi-magic me-1" />Procesar</>}
                      </Button>
                    )}
                  </div>
                </div>
                <Form.Text className="text-muted">Toma foto de una lista escrita o etiqueta de materiales.</Form.Text>
              </>
            )}

            {/* Sin proyecto elegido, el microfono y los botones de procesar
                salen grises y no dicen por que. Con el atajo de
                /tecnico/materiales esto pasa a ser lo primero que ve el tecnico,
                asi que el motivo se escribe donde esta mirando — mismo criterio
                que `blockingReason` en MaterialRequestForm. */}
            {!projectId && (
              <p className="quick-request-shortcut-hint mb-0" role="status">
                <i className="bi bi-info-circle me-1" aria-hidden="true" />
                Primero selecciona el proyecto de arriba.
              </p>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ── Review phase ── */}
      {reviewItems && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="text-muted small">
              {reviewItems.filter(i => !i._removed).length} ítem(s) — revisa antes de enviar
            </span>
            <Button variant="outline-secondary" size="sm" onClick={handleReset}>
              <i className="bi bi-arrow-counterclockwise me-1" />Volver a editar texto
            </Button>
          </div>

          {['matched', 'fuzzy', 'unmatched', 'user_confirmed_new'].map(bucket => {
            const bucketItems = reviewItems
              .map((item, idx) => ({ item, idx }))
              .filter(({ item }) => item.match_status === bucket && !item._removed);
            if (!bucketItems.length) return null;
            const cfg = BUCKET_CONFIG[bucket];
            return (
              <Card key={bucket} className="mb-3 border-0 shadow-sm">
                <Card.Header className={`bg-${cfg.variant} bg-opacity-10 border-0 d-flex align-items-center gap-2`}>
                  <i className={`bi ${cfg.icon} text-${cfg.variant}`} />
                  <span className="fw-semibold">{cfg.label}</span>
                  <Badge bg={cfg.variant} className="ms-1">{bucketItems.length}</Badge>
                </Card.Header>
                <ListGroup variant="flush">
                  {bucketItems.map(({ item, idx }) => (
                    <ListGroup.Item key={idx} className="py-2">
                      <Row className="align-items-center g-2">
                        <Col xs={12} sm={5}>
                          <div className="fw-semibold small">{item.name}</div>
                          {item.matched_inventory && (
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                              Inventario: {item.matched_inventory.name}
                              {item.fulfillment_type && (
                                <Badge bg={FULFILLMENT_BADGE[item.fulfillment_type]?.bg || 'secondary'}
                                  className="ms-1" style={{ fontSize: '0.65rem' }}>
                                  {FULFILLMENT_BADGE[item.fulfillment_type]?.text}
                                </Badge>
                              )}
                            </div>
                          )}
                          {item.ai_confidence != null && item.ai_confidence < 0.7 && (
                            <div className="text-warning" style={{ fontSize: '0.7rem' }}>
                              <i className="bi bi-exclamation-triangle me-1" />
                              Confianza baja — verifica la cantidad
                            </div>
                          )}
                        </Col>
                        <Col xs={6} sm={3}>
                          {/* #356: extraction now returns qty/unit as null when the
                              líder never stated them, instead of inventing "1 unidad".
                              Render that gap as a visible blank the tech must fill —
                              never as a plausible-looking default. */}
                          <InputGroup size="sm">
                            <Form.Control
                              type="number"
                              min="0.001"
                              step="1"
                              placeholder="?"
                              isInvalid={item.qty == null}
                              value={item.qty ?? ''}
                              onChange={e => {
                                const parsed = parseFloat(e.target.value);
                                updateItem(idx, { qty: Number.isFinite(parsed) ? parsed : null });
                              }}
                            />
                            <InputGroup.Text
                              style={{ fontSize: '0.75rem' }}
                              className={item.unit ? undefined : 'text-danger fst-italic'}
                              title={item.unit ? undefined : 'El líder no dijo la unidad — confirmá antes de comprar (#356)'}
                            >
                              {item.unit || 'sin unidad'}
                            </InputGroup.Text>
                          </InputGroup>
                        </Col>
                        <Col xs={6} sm={4} className="d-flex gap-1 justify-content-end">
                          {bucket === 'fuzzy' && (
                            <Button size="sm" variant="outline-warning"
                              title="Marcar como ítem nuevo"
                              onClick={() => updateItem(idx, { match_status: 'user_confirmed_new', inventory_id: null, fulfillment_type: 'needs_purchase' })}>
                              <i className="bi bi-x-lg" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline-danger"
                            title="Eliminar"
                            onClick={() => updateItem(idx, { _removed: true })}>
                            <i className="bi bi-trash" />
                          </Button>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            );
          })}

          <Button
            variant="success"
            className="w-100 mt-2"
            onClick={handleSubmit}
            disabled={submitting || reviewItems.every(i => i._removed)}
          >
            {submitting
              ? <><Spinner size="sm" className="me-2" />Enviando…</>
              : <><i className="bi bi-send me-2" />Enviar solicitud</>}
          </Button>
        </>
      )}
    </Container>
  );
}

// ── Client-side inventory matching ──────────────────────────────────────────

// #599 — este es el gemelo web de `match_inventory_items`, y traia el MISMO
// defecto: la lista de candidatos salia de un solo `ilike('name', '%<la frase
// entera>%')`, o sea una subcadena contigua. Con una preposicion de mas
// ("uniones de 0.5" contra `Uniones 0.5`) volvia VACIA, y entonces `scoreMatch`
// —que esta ahi abajo, escrito y listo— no se ejecutaba nunca. Ahora el catalogo
// activo se lee UNA vez y el filtro es el token-AND normalizado compartido.
async function matchItems(extractedItems) {
  const results = [];

  const { data: catalog } = await supabase
    .from('inventory')
    .select('id, name, sku, unit, current_stock')
    .eq('is_active', true)
    .order('current_stock', { ascending: false });
  const activeCatalog = catalog || [];

  for (const item of extractedItems) {
    const term = item.name?.trim();
    if (!term) {
      results.push({ ...item, match_status: 'unmatched', fulfillment_type: 'needs_purchase' });
      continue;
    }

    const scored = findInventoryCandidates(activeCatalog, term)
      .map(c => ({ ...c, score: scoreMatch(term, c.name) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    const best = scored[0];
    let match_status = 'unmatched';
    let matched_inventory = null;
    let inventory_id = null;
    let fulfillment_type = 'needs_purchase';

    if (best) {
      if (best.score >= 0.65) {
        match_status = 'matched';
        matched_inventory = best;
        inventory_id = best.id;
        fulfillment_type = best.current_stock >= item.qty ? 'from_stock' : 'needs_purchase';
      } else if (best.score >= 0.35) {
        match_status = 'fuzzy';
        matched_inventory = best;
        inventory_id = best.id;
        fulfillment_type = best.current_stock >= item.qty ? 'from_stock' : 'needs_purchase';
      }
    }

    results.push({
      ...item,
      match_status,
      matched_inventory,
      inventory_id,
      fulfillment_type,
      confidence: item.confidence,
    });
  }

  return results;
}

function scoreMatch(query, candidate) {
  const q = query.toLowerCase().trim();
  const c = candidate.toLowerCase().trim();
  if (q === c) return 1.0;
  if (c.startsWith(q) || q.startsWith(c)) return 0.9;
  if (c.includes(q) || q.includes(c)) return 0.75;
  const qWords = q.split(/\s+/).filter(w => w.length > 2);
  const cWords = c.split(/\s+/).filter(w => w.length > 2);
  if (!qWords.length) return 0.1;
  const hits = qWords.filter(w => cWords.some(cw => cw.includes(w) || w.includes(cw)));
  return (hits.length / qWords.length) * 0.65;
}
